import Geographic from '@/lib/geographic'

export interface CameraProperties {
  /**
   * Geographic location point of camera.
   */
  location: Geographic

  /**
   * Horizontal angle in degrees.
   */
  bearing: number

  /**
   * Vertical angle in degrees.
   */
  pitch: number

  /**
   * Array of padding numbers [top, right, bottom, left]
   */
  padding: number[]

  /**
   * Altitude in meters.
   */
  altitude: number

  /**
   * Visible center of the map.
   */
  visibleCenter?: Geographic
}

/**
 * Camera class is the representation of a view in the map viewer.
 * Including all camera positioning properties for 3d environments.
 */
export default class Camera implements CameraProperties {
  readonly location: Geographic
  readonly bearing: number
  readonly pitch: number
  readonly padding: number[]
  readonly altitude: number
  readonly visibleCenter?: Geographic

  /**
   * Initializes a Camera.
   * @param cameraOpions object of camera options to apply
   */
  constructor({
    location = new Geographic(),
    bearing = 0,
    pitch = 0,
    padding = [],
    altitude = 0,
    visibleCenter,
  }: Partial<CameraProperties> = {}) {
    this.location = location
    this.bearing = bearing
    this.pitch = pitch
    this.padding = padding
    this.altitude = altitude
    this.visibleCenter = visibleCenter

    // Camera is immutable
    Object.freeze(this)
  }

  /**
   * Saves camera state to local storage, segmented per app
   */
  saveToLocalStorage() {
    localStorage.setItem(`fish_lastViewedLocation`, JSON.stringify(this))
  }

  /**
   * Returns a camera with state populated from local storage, segmented per app.  If no local
   * storage value exists, null will be returned.
   */
  static fromLocalStorage(): Camera | null {
    const cameraString = localStorage.getItem(`fish_lastViewedLocation`)
    if (!cameraString) {
      return null
    }

    const cameraJson = JSON.parse(cameraString)
    const { latitude, longitude, height } = cameraJson.location
    const location = new Geographic(longitude, latitude, height)

    const {
      bearing = 0,
      pitch = 0,
      padding = [],
      altitude = 0,
      visibleCenter,
    } = cameraJson

    return new Camera({
      location,
      bearing,
      pitch,
      padding,
      altitude,
      visibleCenter,
    })
  }

  /**
   * Returns new camera instance populated with the given visible center.
   */
  withVisibleCenter(visibleCenter: Geographic) {
    const { location, bearing, pitch, padding, altitude } = this

    return new Camera({
      location,
      bearing,
      pitch,
      padding,
      altitude,
      visibleCenter,
    })
  }

  /**
   * Returns a clone of this Geographic object
   */
  clone() {
    return new Camera({ ...this })
  }
}
